import {
    FETCH_NOTIFICATIONS_LOADING,
    FETCH_NOTIFICATIONS_SUCCESS,
    FETCH_NOTIFICATIONS_ERROR,
    MARK_NOTIFICATION_READ,
} from './types';

const initialState = {
    items: [],
    unreadCount: 0,
    isLoading: false,
};

const sameGroupIds = (current = [], next = []) =>
    current.length === next.length && current.every((id) => next.includes(id));

// True when a freshly-polled list is indistinguishable from the one already in state, as far as
// anything downstream depends on it. Compares only what is actually consumed — the badge count and,
// per row, the fields the bell renders or acts on: identity, mapped icon/text, read state, and the
// ids of the collapsed runs behind the row. `isRead` and `groupIds` are load-bearing, not cosmetic:
// miss the first and a read row is treated as unchanged (badge freezes), miss the second and a click
// marks a stale set of runs read. `groupIds` is compared by membership rather than by count — a
// count match is only safe while ids stay in lockstep with the representative row, which nothing
// actually enforces, and comparing two or three ids is cheaper than depending on that. Deliberately
// ignores `title`/`updatedAt`, which churn without the bell ever showing them.
const isSameNotificationList = (state, payload) => {
    if (state.unreadCount !== payload.unreadCount) return false;
    if (state.items.length !== payload.items.length) return false;
    return state.items.every((current, index) => {
        const next = payload.items[index];
        return current.backgroundTaskId === next.backgroundTaskId
            && current.taskType === next.taskType
            && current.isRead === next.isRead
            && sameGroupIds(current.groupIds, next.groupIds);
    });
};

const notificationsReducer = (state = initialState, { type, payload }) => {
    switch (type) {
        case FETCH_NOTIFICATIONS_LOADING:
            return { ...state, isLoading: true };
        case FETCH_NOTIFICATIONS_SUCCESS:
            // A poll that changed nothing keeps the EXISTING `items` array rather than swapping in an
            // equivalent copy, so subscribers re-render on real changes only instead of once per tick.
            // It is the preserved `items` reference that does the work (that is what the selector
            // compares) — not the identity of this state object, which is why `isLoading` can still be
            // settled here instead of being left stuck on from the preceding LOADING action.
            if (isSameNotificationList(state, payload)) {
                return state.isLoading ? { ...state, isLoading: false } : state;
            }
            return { items: payload.items, unreadCount: payload.unreadCount, isLoading: false };
        case FETCH_NOTIFICATIONS_ERROR:
            return { ...state, isLoading: false };
        case MARK_NOTIFICATION_READ: {
            // Flip the one row read and decrement the badge, guarding against double-clicks
            // (only an actually-unread row lowers the count) and never going below zero.
            const wasUnread = state.items.some((n) => n.backgroundTaskId === payload && !n.isRead);
            const items = state.items.map((n) =>
                n.backgroundTaskId === payload ? { ...n, isRead: true } : n);
            return {
                ...state,
                items,
                unreadCount: Math.max(0, state.unreadCount - (wasUnread ? 1 : 0)),
            };
        }
        default:
            return state;
    }
};

export default notificationsReducer;
